import { apiClient } from './client';
import type { AccountTransaction, PageData, TransactionQuery } from './types';

function padDatePart(value: unknown): string {
  return String(value).padStart(2, '0');
}

function monthNumber(value: unknown): string {
  if (typeof value === 'number') return padDatePart(value);
  if (typeof value === 'string') {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return padDatePart(numeric);
    const months = [
      'JANUARY',
      'FEBRUARY',
      'MARCH',
      'APRIL',
      'MAY',
      'JUNE',
      'JULY',
      'AUGUST',
      'SEPTEMBER',
      'OCTOBER',
      'NOVEMBER',
      'DECEMBER',
    ];
    const index = months.indexOf(value.toUpperCase());
    if (index >= 0) return padDatePart(index + 1);
  }
  return padDatePart(value);
}

function normalizeBizDate(value: unknown): string {
  if (typeof value === 'string') {
    const match = value.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (match) return `${match[1]}-${padDatePart(match[2])}-${padDatePart(match[3])}`;
    return value;
  }

  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value;
    return `${year}-${monthNumber(month)}-${padDatePart(day)}`;
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const nestedDate = record.date ?? record.value ?? record.$date;
    if (nestedDate !== undefined) return normalizeBizDate(nestedDate);
    const year = record.year;
    const month = record.month ?? record.monthValue;
    const day = record.day ?? record.dayOfMonth;
    if (year !== undefined && month !== undefined && day !== undefined) {
      return `${year}-${monthNumber(month)}-${padDatePart(day)}`;
    }
  }

  return '';
}

export function txDirection(tx: AccountTransaction): 'credit' | 'debit' | 'neutral' {
  if (tx.reversed) return 'neutral';
  const before = parseFloat(tx.balanceBefore ?? '');
  const after = parseFloat(tx.balanceAfter ?? '');
  if (!isNaN(before) && !isNaN(after)) {
    if (after > before) return 'credit';
    if (after < before) return 'debit';
  }
  if (tx.type === 1) return 'credit';
  if (tx.type === 2) return 'debit';
  return 'neutral';
}

export function normalizeTransaction(tx: AccountTransaction): AccountTransaction {
  const rawTx = tx as AccountTransaction & Record<string, unknown>;
  return {
    ...tx,
    bizDate: normalizeBizDate(
      rawTx.bizDate ?? rawTx.biz_date ?? rawTx.bizDay ?? rawTx.biz_day ?? rawTx.businessDate,
    ),
    createTime: (rawTx.createTime ??
      rawTx.create_time ??
      rawTx.createdTime ??
      rawTx.created_time ??
      rawTx.gmtCreate ??
      rawTx.gmt_create) as string | null | undefined,
    amount: tx.amount == null ? '' : String(tx.amount),
    pkg: tx.pkg ?? tx.appPackage ?? tx.app ?? null,
    app: tx.app ?? tx.pkg ?? tx.appPackage,
    reversed: tx.reversed ?? false,
    reversalOfId: tx.reversalOfId ?? null,
  };
}

export function normalizeTransactionPage(
  page: PageData<AccountTransaction>,
): PageData<AccountTransaction> {
  return { ...page, records: page.records?.map(normalizeTransaction) ?? [] };
}

export async function listAdAccountTransactions(
  query: TransactionQuery = {},
): Promise<PageData<AccountTransaction>> {
  const res = await apiClient.get<PageData<AccountTransaction>>(
    '/ads-admin/ad-account-transactions',
    { params: query },
  );
  return normalizeTransactionPage(res.data);
}

export interface UpdateTransactionDto {
  bizDate?: string;
  amount?: string;
  pkg?: string;
  campaignName?: string;
  externalRef?: string;
  remark?: string;
}

export async function updateTransaction(
  accountId: string,
  transactionId: number,
  dto: UpdateTransactionDto,
): Promise<void> {
  await apiClient.put(`/ads-admin/ad-accounts/${accountId}/transactions/${transactionId}`, dto);
}

export async function reverseTransaction(accountId: string, transactionId: number): Promise<void> {
  await apiClient.post(`/ads-admin/ad-accounts/${accountId}/transactions/${transactionId}/reverse`);
}
